import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiAlertCircle, FiClock, FiFileText, FiTruck } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import useReportData from "../../useReportData";

const CriticalStockReorderReport = () => {
  const gridRef = useRef(null);
  const [data] = useReportData("ReorderAlerts");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [triggerFilter, setTriggerFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalAlerts = data.length;
    const criticalEmergencyCount = data.filter((r) => r.runwayDays < 2.0).length;
    const autoDraftReqCount = data.filter(
      (r) => r.actionTrigger === "Auto-Draft Requisition",
    ).length;
    const inTransitCount = data.filter((r) => r.actionTrigger === "PO Already In-Transit").length;

    return {
      totalAlerts,
      criticalEmergencyCount,
      autoDraftReqCount,
      inTransitCount,
    };
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Material SKU & Name",
        field: "materialSkuName",
        colId: "materialSkuName",
        flex: 2,
        minWidth: 240,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "var(--color-primary, #0b6462)" }}>{p.value}</span>
        ),
      },
      {
        headerName: "Project Site",
        field: "projectSite",
        colId: "projectSite",
        flex: 1.5,
        minWidth: 180,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</span>,
      },
      {
        headerName: "Minimum Threshold (Reorder Level)",
        field: "minThreshold",
        colId: "minThreshold",
        flex: 1.4,
        minWidth: 165,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "#475569" }}>
            {p.value !== undefined ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Current Physical Balance",
        field: "currentBalance",
        colId: "currentBalance",
        flex: 1.3,
        minWidth: 155,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#dc2626" }}>
            {p.value !== undefined ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Shortfall Quantity",
        field: "shortfallQty",
        colId: "shortfallQty",
        flex: 1.3,
        minWidth: 145,
        cellRenderer: (p) => (
          <span
            style={{
              fontWeight: 700,
              background: "#fee2e2",
              color: "#991b1b",
              padding: "2px 8px",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            -{p.value !== undefined ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Avg Daily Consumption",
        field: "dailyConsumption",
        colId: "dailyConsumption",
        flex: 1.3,
        minWidth: 145,
      },
      {
        headerName: "Days Runway Remaining",
        field: "runwayDays",
        colId: "runwayDays",
        flex: 1.3,
        minWidth: 150,
        cellRenderer: (p) => {
          if (p.value == null) {
            return <span style={{ color: "#94a3b8" }}>No recent issues</span>;
          }
          const days = Number(p.value);
          const isDanger = days <= 2.0;
          return (
            <span
              style={{
                fontWeight: 700,
                color: isDanger ? "#dc2626" : "#d97706",
              }}
            >
              {days.toFixed(1)} days
            </span>
          );
        },
      },
      {
        headerName: "Action Trigger",
        field: "actionTrigger",
        colId: "actionTrigger",
        flex: 1.6,
        minWidth: 180,
        cellRenderer: (p) => {
          const val = p.value || "Auto-Draft Requisition";
          let type = "warning";
          if (val === "Auto-Draft Requisition") {
            type = "danger";
          } else if (val === "PO Already In-Transit") {
            type = "info";
          }
          return <StatusChip status={val} type={type} />;
        },
      },
      {
        headerName: "Severity",
        field: "status",
        colId: "status",
        flex: 1,
        minWidth: 110,
        cellRenderer: (p) => {
          const val = p.value || "Critical";
          const type = val === "Critical" ? "danger" : "warning";
          return <StatusChip status={val} type={type} />;
        },
      },
    ];
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (item.materialSkuName && item.materialSkuName.toLowerCase().includes(q)) ||
        (item.projectSite && item.projectSite.toLowerCase().includes(q)) ||
        (item.actionTrigger && item.actionTrigger.toLowerCase().includes(q));

      const matchesStatus =
        statusFilter === "all" || item.status?.toLowerCase() === statusFilter.toLowerCase();

      const matchesTrigger =
        triggerFilter === "all" ||
        item.actionTrigger?.toLowerCase() === triggerFilter.toLowerCase();

      return matchesSearch && matchesStatus && matchesTrigger;
    });
  }, [data, searchTerm, statusFilter, triggerFilter]);

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Critical_Stock_Reorder_Alerts_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Critical stock reorder report exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL REORDER ALERTS"
            value={`${kpis.totalAlerts} SKUs`}
            subtitle="Below safety buffer"
            icon={FiAlertCircle}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="EMERGENCY RUNWAY (<2 DAYS)"
            value={`${kpis.criticalEmergencyCount} Items`}
            subtitle="High risk of project stoppage"
            icon={FiClock}
            color="#dc2626"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="AUTO-DRAFT REQUISITIONS"
            value={`${kpis.autoDraftReqCount} Materials`}
            subtitle="Require indent to procurement"
            icon={FiFileText}
            color="#ea580c"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="ORDERS ALREADY IN-TRANSIT"
            value={`${kpis.inTransitCount} Shipments`}
            subtitle="Expected on site shortly"
            icon={FiTruck}
            color="#0284c7"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (General Journal style) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Critical Stock & Reorder"
            >
              Critical Stock & Reorder
            </div>
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search material SKU, site, trigger..."
            />

            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ maxWidth: "240px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={triggerFilter}
                  onChange={(e) => setTriggerFilter(e.target.value)}
                >
                  <option value="all">All Action Triggers</option>
                  <option value="auto-draft requisition">Auto-Draft Requisition</option>
                  <option value="po already in-transit">PO Already In-Transit</option>
                </select>
                <label>Action Trigger</label>
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ maxWidth: "180px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="low">Low</option>
                </select>
                <label>Severity</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CriticalStockReorderReport;
